<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import { routes } from '@app/router/routes'

import Drawer from '@uiKit/elements/Drawer.vue'
import Dialog from '@uiKit/elements/Dialog.vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'

import InvestPanel, { type PlacedOrder } from '../components/InvestPanel.vue'
import type { PlaceOrderPayload } from '../components/InvestForm.vue'

import { usePlaceOrderMutation, useCalculateFeesQuery } from '@modules/orders'
import { fetchTermsAndConditions } from '@modules/auth'
import { openHtmlInNewTab, getIdempotencyKey, formatAed, formatToInternational } from '@shared/utils'
import type { AppError } from '@core/api/normalizeError'

import { usePropertyDetail } from '../../property'
import { useInvestEligibility } from '../composables/useInvestEligibility'

// Error codes the BE may emit on POST /orders. Source of truth:
// investor-service/src/core/exceptions/errors/order.errors.ts
const ORDER_ERROR_MESSAGES: Record<string, string> = {
  ORDER_INSUFFICIENT_WALLET_BALANCE:
    'Your wallet balance is too low to complete this investment. Add funds to continue.',
  WALLET_INSUFFICIENT_BALANCE:
    'Your wallet balance is too low to complete this investment. Add funds to continue.',
  ORDER_OFFERING_NOT_AVAILABLE: 'This offering is no longer available for investment.',
  ORDER_INSUFFICIENT_TOKENS: 'Not enough tokens available to fulfil this order.',
  PROPERTY_FULLY_FUNDED: 'This property is fully funded.',
  ORDER_BELOW_MIN_INVESTMENT: 'The investment amount is below the minimum for this offering.',
  ORDER_EXCEEDS_MAX_INVESTMENT: 'The investment amount exceeds the maximum for this offering.',
  ORDER_EXCEEDS_MAX_ALLOCATION:
    'This exceeds the maximum allocation allowed for a single investor on this offering.',
  ORDER_INVALID_TOKEN_QUANTITY: 'Token quantity must be a positive whole number.',
  ORDER_INVALID_IDEMPOTENCY_KEY: 'Please retry placing your order.',
  ACCOUNT_NOT_READY_FOR_TRANSACTION:
    'Your account is not ready for transactions. Complete KYC, activate your wallet, and verify your bank account.',
  WALLET_NOT_ACTIVE: 'Your crypto wallet is not active yet.',
  BANK_ACCOUNT_NOT_FOUND: 'Add a verified bank account before placing an order.',
  BANK_ACCOUNT_NOT_VERIFIED: 'Your bank account is pending verification.',
}

const INSUFFICIENT_BALANCE_CODES = new Set([
  'ORDER_INSUFFICIENT_WALLET_BALANCE',
  'WALLET_INSUFFICIENT_BALANCE',
])

const resolveOrderError = (error: AppError): { message: string; isInsufficientBalance: boolean } => {
  const code = error.errorCode
  const friendly = code ? ORDER_ERROR_MESSAGES[code] : undefined
  return {
    message: friendly ?? error.message ?? 'Failed to place order.',
    isInsufficientBalance: !!code && INSUFFICIENT_BALANCE_CODES.has(code),
  }
}

type DrawerState = 'form' | 'submitting' | 'success'

interface Props {
  open: boolean
  /**
   * Property id this drawer invests in. Parent must re-key the drawer
   * (`:key="propertyId"`) when this changes between rows — `usePropertyDetail`
   * and `useInvestEligibility` snapshot their argument at mount, so a fresh
   * propertyId needs a fresh mount. Within one mount the value is stable.
   */
  propertyId: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'order-placed'): void
}>()

const router = useRouter()

const placeOrderMutation = usePlaceOrderMutation()

const {
  loading: propertyLoading,
  error: propertyError,
  offeringId,
  gallery,
  header,
  calculator: propertyCalculator,
} = usePropertyDetail(props.propertyId)

// Token count is lifted to the container so it can drive the calculate-fees
// query. InvestPanel/InvestForm receive it as a v-model.
const tokenCount = ref(1)

const calculateFeesQuery = useCalculateFeesQuery(
  () => offeringId.value,
  () => tokenCount.value,
)

const calculator = computed(() => {
  const fees = calculateFeesQuery.data.value
  const base = propertyCalculator.value

  if (!fees) {
    // Fallback until first query resolves — use property-detail baseline.
    return {
      tokenPrice: base.tokenPrice,
      minTokens: base.minTokens,
      maxTokens: base.tokenAvailable,
      tokenAvailable: base.tokenAvailable,
      grossAmount: tokenCount.value * base.tokenPrice,
      netAmount: tokenCount.value * base.tokenPrice * (1 + base.feePercent / 100),
      platformFeeAmount: 0,
      dldFeeAmount: 0,
      acquisitionFeeAmount: 0,
      tokenisationFeeAmount: 0,
      feesBreakdown: base.feesBreakdown,
      isFetching: calculateFeesQuery.isFetching.value,
    }
  }

  return {
    tokenPrice: fees.tokenPrice,
    minTokens: fees.minTokens,
    maxTokens: fees.maxTokens,
    tokenAvailable: fees.tokensAvailable,
    grossAmount: fees.grossAmount,
    netAmount: fees.netAmount,
    platformFeeAmount: fees.platformFeeAmount,
    dldFeeAmount: fees.dldFeeAmount,
    acquisitionFeeAmount: fees.acquisitionFeeAmount,
    tokenisationFeeAmount: fees.tokenisationFeeAmount,
    feesBreakdown: fees.feesBreakdown,
    isFetching: calculateFeesQuery.isFetching.value,
  }
})

// Snap tokenCount to minTokens once the first response resolves.
watch(
  () => calculateFeesQuery.data.value?.minTokens,
  (min) => {
    if (min && tokenCount.value < min) tokenCount.value = min
  },
  { immediate: true },
)

// Expose the fees query error message so InvestPanel can block the form.
const feesError = computed(() => {
  const err = calculateFeesQuery.error.value as { message?: string } | null
  return err?.message ?? null
})

const { eligibilityFix, walletBalance } = useInvestEligibility(props.propertyId)

// ── Submit lifecycle ────────────────────────────────────────────────────
const state = ref<DrawerState>('form')
const orderError = ref<string | null>(null)
const placedOrder = ref<PlacedOrder | null>(null)

// Force the form to re-mount on each fresh open so payment method and terms
// acceptance reset. tokenCount also resets to 1 so the query re-fires.
const formInstance = ref(0)

// Idempotency key is stable across in-flight retries of the SAME attempt.
// We mint a fresh one when the user opens the drawer; if mutateAsync rejects
// with a retryable error and the user clicks place-order again, the same key
// is sent — the BE returns the existing order rather than double-debiting.
const currentIdempotencyKey = ref<string>(getIdempotencyKey())
const isInsufficientBalance = ref(false)

// Confirmation step before debiting the wallet. The form's place-order is
// staged here (consequence shown), and only fires the mutation on confirm.
const pendingOrder = ref<PlaceOrderPayload | null>(null)
const confirmOpen = computed(() => pendingOrder.value !== null)
const isSubmitting = computed(() => state.value === 'submitting')
const walletBalanceAfter = computed(() =>
  Math.max(walletBalance.value - (pendingOrder.value?.totalPayable ?? 0), 0),
)

watch(
  () => props.open,
  (open) => {
    if (!open) return
    state.value = 'form'
    orderError.value = null
    placedOrder.value = null
    pendingOrder.value = null
    tokenCount.value = 1
    formInstance.value++
    isInsufficientBalance.value = false
    currentIdempotencyKey.value = getIdempotencyKey()
  },
)

const openTerms = async () => {
  try {
    const data = await fetchTermsAndConditions()
    openHtmlInNewTab(data.content)
  } catch {
    // silent — parity with prior Result<T> non-success path
  }
}

// Form submit only stages the order — the confirmation dialog gates the debit.
const requestPlaceOrder = (payload: PlaceOrderPayload) => {
  pendingOrder.value = payload
}

const cancelConfirm = () => {
  pendingOrder.value = null
}

const confirmPlaceOrder = async () => {
  if (!pendingOrder.value) return
  await placeStagedOrder(pendingOrder.value)
  pendingOrder.value = null
}

const placeStagedOrder = async (payload: PlaceOrderPayload) => {
  orderError.value = null
  isInsufficientBalance.value = false
  state.value = 'submitting'

  try {
    const order = await placeOrderMutation.mutateAsync({
      offeringId: offeringId.value,
      tokenQuantity: payload.tokens,
      idempotencyKey: currentIdempotencyKey.value,
      acceptedTerms: true,
    })
    placedOrder.value = {
      id: order.id,
      orderReference: order.orderReference,
      tokens: payload.tokens,
      // Prefer BE-confirmed amounts (decimal-string → number) over the
      // client-side estimate; the BE is the source of truth post-debit.
      totalPayable: Number(order.netAmount) || payload.totalPayable,
      walletAmount: Number(order.walletAmount) || payload.totalPayable,
      pgAmount: Number(order.pgAmount) || 0,
      paymentMethod: payload.paymentMethod,
    }
    state.value = 'success'
    // Funding progress / availability changed — let the host list refetch
    // while the in-drawer receipt stays open for the user.
    emit('order-placed')
  } catch (e) {
    const resolved = resolveOrderError(e as AppError)
    orderError.value = resolved.message
    isInsufficientBalance.value = resolved.isInsufficientBalance
    state.value = 'form'
  }
}

const onAddFunds = () => {
  router.push(routes.WALLET)
  emit('close')
}

const viewReceipt = () => {
  if (!placedOrder.value) return
  router.push({
    path: routes.ORDER_SUCCESS.replace(':id', props.propertyId),
    query: {
      tokens: String(placedOrder.value.tokens),
      tokenPrice: String(calculator.value.tokenPrice),
      investmentValue: String(placedOrder.value.tokens * calculator.value.tokenPrice),
      totalPayable: String(placedOrder.value.totalPayable),
      walletAmount: String(placedOrder.value.walletAmount),
      pgAmount: String(placedOrder.value.pgAmount),
      paymentMethod: placedOrder.value.paymentMethod,
      propertyName: header.value.title,
      orderId: placedOrder.value.id,
      orderReference: placedOrder.value.orderReference,
    },
  })
  emit('close')
}

const close = () => emit('close')
</script>

<template>
  <Drawer
    :model-value="open"
    position="right"
    width="540px"
    :show-close-icon="false"
    @update:model-value="(v) => !v && close()"
  >
    <template #header>
      <Box align="center" justify="between" gap="2" class="invest-drawer__header">
        <Text type="page-title">Invest now</Text>
        <Box
          is="button"
          align="center"
          justify="center"
          type="button"
          class="invest-drawer__close"
          aria-label="Close"
          @click="close"
        >
          <PiIcon name="times" />
        </Box>
      </Box>
    </template>

    <InvestPanel
      :loading="propertyLoading"
      :property-error="!!propertyError"
      :state="state"
      :placed-order="placedOrder"
      :eligibility-fix="eligibilityFix"
      :fees-error="feesError"
      :gallery-main-image="gallery.mainImage"
      :header-title="header.title"
      :header-location="header.location"
      :calculator="calculator"
      :wallet-balance="walletBalance"
      :order-error="orderError"
      :is-insufficient-balance="isInsufficientBalance"
      :form-instance-key="formInstance"
      :token-count="tokenCount"
      @update:token-count="(v) => (tokenCount = v)"
      @eligibility-cta="eligibilityFix?.onCta()"
      @place-order="requestPlaceOrder"
      @open-terms="openTerms"
      @view-receipt="viewReceipt"
      @success-done="close"
      @close="close"
      @add-funds="onAddFunds"
    />
  </Drawer>

  <Dialog
    :model-value="confirmOpen"
    width="400px"
    :show-close-icon="false"
    :dismissable="!isSubmitting"
    @update:model-value="(v) => !v && cancelConfirm()"
  >
    <Box direction="col" gap="4" class="invest-confirm">
      <Box direction="col" gap="1">
        <Text type="title-lg" weight="bold">Confirm your investment</Text>
        <Text type="body-sm" color="muted">
          Funds will be debited from your wallet once you confirm.
        </Text>
      </Box>

      <Box direction="col" gap="3" class="invest-confirm__rows">
        <Box align="center" justify="between" gap="3">
          <Text type="body-sm" is="span" color="muted">Property</Text>
          <Text type="body-sm" is="span" weight="semibold" align="right" :truncate="true">
            {{ header.title }}
          </Text>
        </Box>
        <Box align="center" justify="between" gap="3">
          <Text type="body-sm" is="span" color="muted">Tokens</Text>
          <Text type="body-sm" is="span" weight="semibold">
            {{ formatToInternational(pendingOrder?.tokens ?? 0) }}
          </Text>
        </Box>
        <Box align="center" justify="between" gap="3">
          <Text type="body-sm" is="span" color="muted">Total payable</Text>
          <Text type="body-sm" is="span" weight="bold" color="primary">
            {{ formatAed(pendingOrder?.totalPayable ?? 0) }}
          </Text>
        </Box>
        <Box align="center" justify="between" gap="3">
          <Text type="body-sm" is="span" color="muted">Wallet balance after</Text>
          <Text type="body-sm" is="span" weight="semibold">{{ formatAed(walletBalanceAfter) }}</Text>
        </Box>
      </Box>

      <Box direction="col" gap="2">
        <Button
          label="Confirm & place order"
          icon="check"
          size="large"
          block
          :loading="isSubmitting"
          @click="confirmPlaceOrder"
        />
        <Button
          label="Cancel"
          variant="outline"
          size="large"
          block
          :disabled="isSubmitting"
          @click="cancelConfirm"
        />
      </Box>
    </Box>
  </Dialog>
</template>

<style scoped>
.invest-drawer__header {
  width: 100%;
}

.invest-drawer__close {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: 1px solid var(--p-content-border-color);
  background: var(--p-surface-0);
  cursor: pointer;
  color: var(--p-text-muted-color);
  transition:
    background 0.15s,
    color 0.15s;
}

.invest-drawer__close:hover {
  background: var(--p-surface-100);
  color: var(--p-text-color);
}
</style>
